import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { DialogProps } from '../../Dialogs/DialogProps';
import styles from './_application-configuration.module.scss';
import { IIncomePeriod } from "../../../Interfaces/Common/IIncomePeriod";
import { IState } from '../../../Interfaces/Common/IState';
import { IHousingStatus } from '../../../Interfaces/Customer/IHousingStatus';
import { IIncomeType } from '../../../Interfaces/Common/IIncomeType';
import { ICustomField } from '../../../Interfaces/Common/ICustomField';
import {
  ApplicationFormConfig,
  CreditApplicationConfigurationEditor, LookupItem
} from "@amsanalytics/react-shared-library";
import { AppState } from '../../../store/rootReducer';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';

export interface ApplicationFormResult {
  applicantCreditAppFormConfiguration: ApplicationFormConfig;
  coApplicantCreditAppFormConfiguration: ApplicationFormConfig;
  customFields: ICustomField[];
}

// Return data when the user clicks the SAVE button.
export interface ApplicationFormProps extends DialogProps<ApplicationFormResult> {
  applicantCreditAppFormConfiguration?: ApplicationFormConfig;
  coApplicantCreditAppFormConfiguration?: ApplicationFormConfig;
  customFields?: ICustomField[];
}

export default function ApplicationFormConfiguration(props: ApplicationFormProps) {
  const [applicantCreditAppFormConfiguration, setApplicantCreditAppFormConfiguration] = useState<ApplicationFormConfig | undefined>(props.applicantCreditAppFormConfiguration ? { ...props.applicantCreditAppFormConfiguration } : undefined);
  const [coApplicantCreditAppFormConfiguration, setCoApplicantCreditAppFormConfiguration] = useState<ApplicationFormConfig | undefined>(props.coApplicantCreditAppFormConfiguration ? { ...props.coApplicantCreditAppFormConfiguration }: undefined);
  const [customFields, setCustomFields] = useState<ICustomField[]>(props.customFields ? props.customFields : []);
  // List of marital statuses user can select from.
  const maritalStatuses: LookupItem[] = useSelector((state: AppState) => state.applications.maritalStatuses);
  // List of states user can select from.
  const states: IState[] = useSelector((state: AppState) => state.applications.states);
  // List of housing statuses user can select from.
  const housingStatuses: IHousingStatus[] = useSelector((state: AppState) => state.applications.housingStatuses);
  // List of income periods user can select from.
  const incomePeriods: IIncomePeriod[] = useSelector((state: AppState) => state.applications.incomePeriods);
  // List of income periods user can select from.
  const incomeTypes: IIncomeType[] = useSelector((state: AppState) => state.applications.incomeTypes);
  const relationshipToApplicantTypes: LookupItem[] = useSelector((state: AppState) => state.applications.lookups.relationshipToApplicants);
  const identificationTypes = useSelector((state: AppState) => state.applications.lookups.identificationTypes);
  const [selectedApplicantTab, setSelectedApplicantTab] = useState(0);
  const creditAppLookups = {
    housingStatuses: housingStatuses,
    relationshipTypes: relationshipToApplicantTypes,
    payPeriods: incomePeriods.map((ip: IIncomePeriod) => {
      return {
        id: ip.id,
        name: ip.name,
        description: ip.description,
        sortOrder: ip.sortOrder,
      };
    }),
    incomeTypes: incomeTypes,
    maritalStatuses: maritalStatuses,
    identificationTypes: identificationTypes,
    addressStates: states,
    employmentStates: states,
    identificationStates: states,
    translations: [],
    stores: []
  };
  return (
    <div className={styles.applicationConfigurationContainer}>
      <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
        <h2 style={{ flex: `0 auto`, margin: `10px 10px`, padding: `0` }}>Configure Your Application Form</h2>
      </div>
      <DialogContent className={styles.applicationConfigurationContent} style={{padding: '0px'}}>
        <TabStrip 
          selected={selectedApplicantTab}
          onSelect={(e) => setSelectedApplicantTab(e.selected)}
          renderAllContent={true}
          // keepTabsMounted={true}
        >
          <TabStripTab title="Applicant">
            <CreditApplicationConfigurationEditor
                key={"configuration-editor-applicant"}
                creditApplicationFormConfiguration={applicantCreditAppFormConfiguration}
                lookups={creditAppLookups}
                heightOffset={285}
                onChange={(applicantCreditAppConfig: ApplicationFormConfig) => {
                  // console.log('applicantCreditAppConfig: Before', applicantCreditAppFormConfiguration);
                  setApplicantCreditAppFormConfiguration(applicantCreditAppConfig);
                  // console.log('applicantCreditAppConfig: After', applicantCreditAppConfig);
                }}
              />
          </TabStripTab>
          <TabStripTab title="Co-Applicant">
          <CreditApplicationConfigurationEditor
              key={"configuration-editor-co-applicant"}
              creditApplicationFormConfiguration={coApplicantCreditAppFormConfiguration}
              lookups={creditAppLookups}
              heightOffset={285}
              onChange={(coApplicantCreditAppConfig: ApplicationFormConfig) => {
                // console.log('coApplicantCreditAppConfig: Before', coApplicantCreditAppConfig);
                setCoApplicantCreditAppFormConfiguration(coApplicantCreditAppConfig);
                // console.log('coApplicantCreditAppConfig: After', coApplicantCreditAppConfig);
              }}
            />
          </TabStripTab>
        </TabStrip>
      </DialogContent>
      <DialogActions className={styles.applicationConfigurationActions}>
        <Button autoFocus onClick={props.closeDialog} color='primary' variant='outlined'>
          Cancel
        </Button>
        <Button
          onClick={() => {
            props.accept && props.accept({
              applicantCreditAppFormConfiguration: applicantCreditAppFormConfiguration!,
              coApplicantCreditAppFormConfiguration: coApplicantCreditAppFormConfiguration!,
              customFields: customFields
            });
          }}
          color='primary'
          variant='contained'
        >
          Save
        </Button>
      </DialogActions>
    </div>
  );
}
